import * as React from "react";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

interface NavItem {
  name: string;
  href: string;
  isActive: boolean;
}

interface AnimatedStickyHeaderProps {
  logo: React.ReactNode;
  navItems: NavItem[];
  authButtons: {
    desktop: React.ReactNode;
    mobile: React.ReactNode;
  };
  expandedWidth?: number;
  collapsedWidth?: number;
  height?: number;
}

export const AnimatedStickyHeader: React.FC<AnimatedStickyHeaderProps> = ({
  logo,
  navItems,
  authButtons,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const toggleMenu = () => setIsOpen(!isOpen);

  return (
    <>
      <nav className="w-full bg-white/90 backdrop-blur-sm fixed top-0 left-0 right-0 z-50 border-b border-gray-100/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              {logo}
            </div>

            {/* Desktop Menu - Centered */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className={`text-sm transition-colors font-medium ${
                    item.isActive
                      ? "text-brand-blue"
                      : "text-gray-600 hover:text-brand-blue"
                  }`}
                >
                  {item.name}
                </a>
              ))}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-4">
                {authButtons.desktop}
              </div>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden text-gray-600"
                onClick={toggleMenu}
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 bg-white z-50 pt-24 px-6 md:hidden overflow-y-auto"
            initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, x: "100%" }}
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : { type: "spring", damping: 25, stiffness: 300 }
            }
          >
            <button
              className="absolute top-6 right-6 p-2"
              onClick={toggleMenu}
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <div className="flex flex-col space-y-6">
              {navItems.map((item, i) => (
                <motion.div
                  key={item.name}
                  initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: prefersReducedMotion ? 0 : i * 0.1 + 0.1 }}
                  exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, x: 20 }}
                >
                  <a
                    href={item.href}
                    className={`text-base font-medium ${
                      item.isActive ? "text-brand-blue" : "text-gray-600"
                    }`}
                    onClick={toggleMenu}
                  >
                    {item.name}
                  </a>
                </motion.div>
              ))}

              <motion.div
                initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: prefersReducedMotion ? 0 : 0.5 }}
                exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 20 }}
                className="pt-6"
              >
                {authButtons.mobile}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
